import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import api from '../lib/api';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Users, Target, Calendar as CalendarIcon, 
  AlertCircle, Sparkles, ArrowRight, Crown, Lock
} from 'lucide-react';

export default function JoinSharedGoal() {
  const { inviteCode } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [goalInfo, setGoalInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  // Join form state
  const isPremium = user?.is_premium;

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) return;
    
    if (!user) {
      // Redirect to auth with return URL
      navigate(`/auth?redirect=/join/${inviteCode}`);
      return;
    }
    // Any logged-in user can join - no premium check needed
    fetchGoalInfo();
  }, [inviteCode, user, authLoading, navigate]);

  const fetchGoalInfo = async () => {
    try {
      const res = await api.get(`/api/shared-goals/invite/${inviteCode}`);
      setGoalInfo(res.data);
      
      if (res.data.already_joined) {
        toast.info('You are already part of this goal');
        navigate(`/shared-goals/${res.data.id}`);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('invalid_code');
      } else if (err.response?.status === 400) {
        setError(err.response.data.detail);
      } else {
        setError('Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      const res = await api.post(`/api/shared-goals/join/${inviteCode}`, {});
      
      toast.success(res.data.message || 'Successfully joined!');
      navigate(`/shared-goals/${goalInfo.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join goal');
    } finally {
      setJoining(false);
    }
  };

  // Show loading while auth is being checked
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // Error states
  if (error === 'invalid_code') {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-4 py-16 max-w-lg">
          <Card className="text-center">
            <CardContent className="py-12">
              <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Invalid Invite Code</h2>
              <p className="text-muted-foreground mb-6">
                This invite link is invalid or has expired. 
                Please ask for a new invite link.
              </p>
              <Button variant="outline" onClick={() => navigate('/shared-goals')}>
                Go to Shared Goals
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-4 py-16 max-w-lg">
          <Card className="text-center">
            <CardContent className="py-12">
              <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Unable to Join</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button variant="outline" onClick={() => navigate('/shared-goals')}>
                Go to Shared Goals
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="container mx-auto px-4 py-8 max-w-lg">
        {/* Goal Info Card */}
        <Card className="mb-6" data-testid="goal-info-card">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">You&apos;re Invited!</CardTitle>
            <CardDescription>
              Join this shared goal with your friends
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <h3 className="text-xl font-semibold text-center">{goalInfo.title}</h3>
              {goalInfo.description && (
                <p className="text-muted-foreground text-center text-sm">
                  {goalInfo.description}
                </p>
              )}
              
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="text-center">
                  <Target className="w-5 h-5 mx-auto text-primary mb-1" />
                  <p className="text-sm text-muted-foreground">Target</p>
                  <p className="font-medium">{goalInfo.target_value} {goalInfo.target_unit}</p>
                </div>
                <div className="text-center">
                  <CalendarIcon className="w-5 h-5 mx-auto text-primary mb-1" />
                  <p className="text-sm text-muted-foreground">Deadline</p>
                  <p className="font-medium">{format(new Date(goalInfo.deadline), 'MMM d')}</p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pt-2">
                <Users className="w-4 h-4" />
                <span>
                  {goalInfo.participant_count} / {goalInfo.max_participants} participants
                </span>
              </div>

              <p className="text-center text-sm text-muted-foreground">
                Created by <span className="font-medium">{goalInfo.creator_name}</span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Join Button */}
        <Button 
          className="w-full h-12 text-lg" 
          onClick={handleJoin}
          disabled={joining}
          data-testid="join-goal-btn"
        >
          {joining ? (
            'Joining...'
          ) : (
            <>
              Join Goal
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </Button>

        <p className="text-center text-sm text-muted-foreground mt-4">
          By joining, you commit to achieving this goal with the group
        </p>
      </main>
    </div>
  );
}
